import { clearMetricsQueries } from '../../shared/hooks/metricsQueryPolicy';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi } from './api';
import { qk } from '../../lib/queryKeys';
import { AnalyticsEvent, identifyUser, resetAnalytics, trackEvent } from '../../lib/analytics';
import type { Session } from './api/types';

export const useCreateUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: authApi.createUser,
    onSuccess: () => {
      trackEvent(AnalyticsEvent.SignUp);
      qc.invalidateQueries({ queryKey: qk.me() });
    },
  });
};

export const useLogin = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: authApi.login,
    onSuccess: async () => {
      clearMetricsQueries(qc);
      await qc.refetchQueries({ queryKey: qk.me() });
      // Identify the user and pin a fresh Amplitude session to this login.
      const session = qc.getQueryData<Session>(qk.me());
      if (session?.user) {
        identifyUser(session.user);
        trackEvent(AnalyticsEvent.Login, {
          is_admin: session.is_admin ?? session.user.is_admin ?? false,
        });
      }
    },
  });
};

export const useChangePassword = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: authApi.changePassword,
    onSuccess: async () => {
      await qc.refetchQueries({ queryKey: qk.me() });
    },
  });
};

export const useChangePasswordTemporary = () => {
  return useMutation({
    mutationFn: authApi.changePasswordTemporary,
  });
};

export const useChangePasswordAuthenticated = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: authApi.changePasswordAuthenticated,
    onSuccess: async () => {
      trackEvent(AnalyticsEvent.PasswordChanged);
      await qc.refetchQueries({ queryKey: qk.me() });
    },
  });
};

export const useLogout = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: authApi.logout,
    onSuccess: async () => {
      trackEvent(AnalyticsEvent.Logout);
      resetAnalytics();
      clearMetricsQueries(qc);
      await qc.refetchQueries({ queryKey: qk.me() });
    },
  });
};

export const useForgotPassword = () => {
  return useMutation({
    mutationFn: authApi.forgotPassword,
  });
};

export const useConfirmForgotPassword = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: authApi.confirmForgotPassword,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.me() }),
  });
};

export const useUpdateConsent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: authApi.updateConsent,
    onSuccess: async (_data, variables) => {
      trackEvent(AnalyticsEvent.ConsentDecision, {
        decision: variables.decision,
      });
      await qc.refetchQueries({ queryKey: qk.me() });
    },
  });
};

export const useVerifyUserInvite = () => {
  return useMutation({
    mutationFn: authApi.verifyUserInvite,
  });
};

export const useCompleteUserInvite = () => {
  return useMutation({
    mutationFn: authApi.completeUserInvite,
  });
};

export const useRequestUserInvite = () => {
  return useMutation({
    mutationFn: authApi.requestUserInvite,
  });
};

export const useVerifyPasswordReset = () => {
  return useMutation({
    mutationFn: authApi.verifyPasswordReset,
  });
};

export const useCompletePasswordReset = () => {
  return useMutation({
    mutationFn: authApi.completePasswordReset,
  });
};
